package org.gymly.ui.admin

import androidx.compose.foundation.border
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.strings
import org.gymly.model.AdminAccessEvent
import org.gymly.model.AdminBookingItem
import org.gymly.model.AdminDashboardSummary
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminMembershipItem
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminListRow
import org.gymly.model.AdminRoute
import org.gymly.model.AdminScheduleSlot
import org.gymly.model.AdminTrainer
import org.gymly.model.AdminWorkDaySchedule
import org.gymly.model.AdminWeekday
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
// TODO(IA): reporte ejecutivo suspendido; reactivar con el módulo de IA.
// import org.gymly.ui.components.ExecutiveReportBanner
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.PortalListCard
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.WeekRange
import org.gymly.util.userMessage
import kotlin.math.roundToInt
import androidx.compose.ui.graphics.Color

@Composable
fun AdminDashboardScreen(onNavigate: (AdminRoute) -> Unit) {
    val t = strings()
    var summary by remember { mutableStateOf<AdminDashboardSummary?>(null) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var bookings by remember { mutableStateOf<List<AdminBookingItem>>(emptyList()) }
    var accessEvents by remember { mutableStateOf<List<AdminAccessEvent>>(emptyList()) }
    var exchangeQuote by remember { mutableStateOf<AdminExchangeRateQuote?>(null) }
    var modules by remember { mutableStateOf(AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true)) }
    var selectedTrainerId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        loading = true
        error = null
        try {
            summary = AdminRepo.api.dashboardSummary()
            trainers = AdminRepo.api.listTrainers()
            bookings = AdminRepo.api.listBookings()
            modules = runCatching { AdminRepo.api.getSettingsModules() }
                .getOrDefault(AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true))
            if (modules.exchangePanelVisible) {
                exchangeQuote =runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
            }
            if (modules.accessEnabled) {
                accessEvents = runCatching { AdminRepo.api.listAccessEvents(8) }.getOrDefault(emptyList())
            }
            selectedTrainerId = null
        } catch (e: Throwable) {
            error = e.userMessage(t.dashboard.loadFailed)
        } finally {
            loading = false
        }
    }

    if (loading) {
        GymlyLoadingCenter()
        return
    }

    BoxWithConstraints(Modifier.fillMaxSize().background(AdminColors.Surface).padding(GymlySpace.lg)) {
        val wide = maxWidth >= 1100.dp
        if (wide) {
            Row(Modifier.fillMaxSize(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                VerticalScrollColumn(Modifier.weight(1.4f).fillMaxHeight()) {
                    error?.let { Text(it, color = AdminColors.Error, modifier = Modifier.padding(bottom = 12.dp)) }
                    DashboardQuickActions(onNavigate, compact = false)
                    Spacer(Modifier.height(16.dp))
                    // TODO(IA): reporte ejecutivo suspendido.
                    // ExecutiveReportBanner()
                    // Spacer(Modifier.height(16.dp))
                    ActivityProgressCard(summary)
                    Spacer(Modifier.height(16.dp))
                    ScheduleGridCard(selectedTrainerId, trainers, bookings)
                }
                VerticalScrollColumn(
                    Modifier
                        .width(320.dp)
                        .fillMaxHeight(),
                ) {
                    if (modules.exchangePanelVisible) {
                        ExchangeRateCard(exchangeQuote, compact = false)
                        Spacer(Modifier.height(16.dp))
                    }
                    if (modules.accessEnabled) {
                        CloseDoorManualButton()
                        Spacer(Modifier.height(16.dp))
                        AccessLiveCard(accessEvents)
                        Spacer(Modifier.height(16.dp))
                    }
                    TrainerSidePanel(
                        trainers = trainers,
                        selectedId = selectedTrainerId,
                        onSelect = { selectedTrainerId = it },
                        bookings = bookings,
                        onAddTrainer = { onNavigate(AdminRoute.Workers) },
                    )
                }
            }
        } else {
            VerticalScrollColumn(Modifier.fillMaxSize()) {
                error?.let { Text(it, color = AdminColors.Error, modifier = Modifier.padding(bottom = 12.dp)) }
                DashboardQuickActions(onNavigate, compact = true)
                Spacer(Modifier.height(16.dp))
                // TODO(IA): reporte ejecutivo suspendido.
                // ExecutiveReportBanner()
                // Spacer(Modifier.height(16.dp))
                ActivityProgressCard(summary)
                Spacer(Modifier.height(16.dp))
                ScheduleGridCard(selectedTrainerId, trainers, bookings)
                Spacer(Modifier.height(16.dp))
                if (modules.exchangePanelVisible) {
                    ExchangeRateCard(exchangeQuote, compact = true)
                    Spacer(Modifier.height(16.dp))
                }
                if (modules.accessEnabled) {
                    CloseDoorManualButton()
                    Spacer(Modifier.height(16.dp))
                    AccessLiveCard(accessEvents)
                    Spacer(Modifier.height(16.dp))
                }
                TrainerSidePanel(
                    trainers = trainers,
                    selectedId = selectedTrainerId,
                    onSelect = { selectedTrainerId = it },
                    bookings = bookings,
                    onAddTrainer = { onNavigate(AdminRoute.Workers) },
                )
            }
        }
    }
}

@Composable
private fun DashboardQuickActions(onNavigate: (AdminRoute) -> Unit, compact: Boolean) {
    val t = strings()
    val actions = listOf(
        Triple(t.dashboard.registerSale, t.dashboard.registerSaleHint, AdminRoute.StoreSales),
        Triple(t.dashboard.membershipOrder, t.dashboard.membershipOrderHint, AdminRoute.MembershipOrders),
        Triple(t.dashboard.bulkEntry, t.dashboard.bulkEntryHint, AdminRoute.BulkSignup),
        Triple(t.dashboard.assignMembership, t.dashboard.assignMembershipHint, AdminRoute.Memberships),
        Triple(t.dashboard.membershipPlans, t.dashboard.membershipPlansHint, AdminRoute.MembershipPlans),
        Triple(t.dashboard.attendanceSessions, t.dashboard.attendanceSessionsHint, AdminRoute.Bookings),
    )
    val columns = if (compact) 2 else 3
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(20.dp),
    ) {
        Text(t.dashboard.quickActions, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
        Text(
            t.dashboard.quickActionsHint,
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp, bottom = 14.dp),
        )
        actions.chunked(columns).forEach { row ->
            Row(
                Modifier.fillMaxWidth().padding(bottom = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                row.forEach { (title, subtitle, route) ->
                    QuickActionTile(title, subtitle, Modifier.weight(1f)) { onNavigate(route) }
                }
            }
        }
    }
}

@Composable
private fun QuickActionTile(
    title: String,
    subtitle: String,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Column(
        modifier
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Primary)
            .clickable(onClick = onClick)
            .padding(12.dp),
    ) {
        Text(title, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.OnBrand)
        Text(
            subtitle,
            color = AdminColors.OnBrand.copy(alpha = 0.85f),
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}

@Composable
private fun ActivityProgressCard(summary: AdminDashboardSummary?) {
    val t = strings()
    val s = summary ?: return
    val week = s.bookingsThisWeek.coerceAtLeast(0)
    val today = s.bookingsToday.coerceAtLeast(0)
    val weekProgress = if (week == 0L) 0f else (today.toFloat() / week.toFloat()).coerceIn(0f, 1f)
    val payProgress = (s.onTimePaymentPercent / 100f).coerceIn(0f, 1f)

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(20.dp),
    ) {
        Text(t.dashboard.activity, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
        Spacer(Modifier.height(16.dp))
        ProgressRow(
            title = t.dashboard.bookingsTodayWeek,
            detail = t.dashboard.bookingsDetail(today, week),
            progress = weekProgress,
        )
        Spacer(Modifier.height(14.dp))
        ProgressRow(
            title = t.dashboard.paymentsOnTime,
            detail = t.dashboard.paymentsDetail(s.onTimePaymentPercent),
            progress = payProgress,
        )
        Spacer(Modifier.height(14.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            MiniMetric("${s.activeClients}", t.dashboard.activeClients)
            MiniMetric("${s.trainers}", t.dashboard.trainers)
            MiniMetric("${s.scheduleSlots}", t.dashboard.scheduleSlots)
        }
    }
}

@Composable
private fun ProgressRow(title: String, detail: String, progress: Float) {
    Column {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(title, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
            Text("${(progress * 100).roundToInt()}%", color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        Text(detail, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        LinearProgressIndicator(
            progress = { progress },
            modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(4.dp)),
            color = AdminColors.Primary,
            trackColor = AdminColors.Outline.copy(alpha = 0.35f),
        )
    }
}

@Composable
private fun MiniMetric(value: String, label: String) {
    Column {
        Text(value, fontWeight = FontWeight.Bold, fontSize = 20.sp, color = AdminColors.Primary)
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    }
}

@Composable
private fun CloseDoorManualButton() {
    val t = strings()
    val scope = rememberCoroutineScope()
    var closing by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var failed by remember { mutableStateOf(false) }

    Column(Modifier.fillMaxWidth()) {
        Column(
            Modifier
                .fillMaxWidth()
                .heightIn(min = 140.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(Color(0xFFC62828))
                .clickable(enabled = !closing) {
                    scope.launch {
                        closing = true
                        message = null
                        failed = false
                        try {
                            val r = AdminRepo.api.closeAccessDoor()
                            message = r.message.ifBlank { t.dashboard.doorClosed }
                            failed = !r.ok
                        } catch (e: Throwable) {
                            failed = true
                            message = e.userMessage(t.dashboard.doorCloseFailed)
                        } finally {
                            closing = false
                        }
                    }
                }
                .padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            if (closing) {
                CircularProgressIndicator(
                    modifier = Modifier.size(56.dp),
                    color = Color.White,
                    strokeWidth = 4.dp,
                )
            } else {
                Box(
                    Modifier
                        .size(72.dp)
                        .clip(RoundedCornerShape(16.dp))
                        .background(Color.White.copy(alpha = 0.18f)),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        Icons.Outlined.Lock,
                        contentDescription = t.dashboard.closeDoor,
                        tint = Color.White,
                        modifier = Modifier.size(44.dp),
                    )
                }
            }
            Spacer(Modifier.height(12.dp))
            Text(
                t.dashboard.closeDoor,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = Color.White,
            )
            Text(
                t.dashboard.closeDoorHint,
                color = Color.White.copy(alpha = 0.9f),
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        message?.let { msg ->
            Text(
                msg,
                color = if (failed) AdminColors.Error else AdminColors.Success,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 8.dp),
            )
        }
    }
}

@Composable
private fun AccessLiveCard(events: List<AdminAccessEvent>) {
    val t = strings()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(20.dp),
    ) {
        Text(t.dashboard.accessTitle, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
        Spacer(Modifier.height(4.dp))
        Text(
            t.dashboard.accessHint,
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Spacer(Modifier.height(12.dp))
        if (events.isEmpty()) {
            Text(
                t.dashboard.accessEmpty,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
        } else {
            events.take(8).forEach { ev ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 5.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text(
                            ev.clientName ?: t.dashboard.unknownPerson,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp,
                        )
                        Text(ev.resultLabel, color = AdminColors.TextMuted, fontSize = 12.sp)
                    }
                    Text(
                        if (ev.success) "OK" else t.common.emptyValue,
                        color = if (ev.success) AdminColors.Success else AdminColors.Error,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
    }
}

@Composable
private fun ScheduleGridCard(
    selectedTrainerId: String?,
    trainers: List<AdminTrainer>,
    bookings: List<AdminBookingItem>,
) {
    val t = strings()
    var weekOffset by remember { mutableStateOf(0) }
    val week = remember(weekOffset) { WeekRange.ofOffset(weekOffset) }
    var companyHours by remember { mutableStateOf<List<AdminWorkDaySchedule>>(emptyList()) }
    var schedules by remember { mutableStateOf<List<AdminScheduleSlot>>(emptyList()) }
    var memberships by remember { mutableStateOf<List<AdminMembershipItem>>(emptyList()) }
    var loadingGrid by remember { mutableStateOf(true) }
    val absentColor = Color(0xFFC62828)

    LaunchedEffect(week.mondayIso) {
        loadingGrid = true
        companyHours = runCatching { AdminRepo.api.getCompany().operatingHours }.getOrDefault(emptyList())
        schedules = runCatching { AdminRepo.api.listSchedules(week.mondayIso) }.getOrDefault(emptyList())
        memberships = runCatching { AdminRepo.api.listMemberships() }.getOrDefault(emptyList())
        loadingGrid = false
    }

    val activeTrainerIds = remember(trainers) { trainers.map { it.id }.toSet() }
    val filtered = remember(schedules, selectedTrainerId, activeTrainerIds) {
        schedules
            .filter { slot ->
                val trainerActive = slot.trainerId == null || slot.trainerId in activeTrainerIds
                val matchesSelection =
                    selectedTrainerId == null ||
                        slot.trainerId == null ||
                        slot.trainerId == selectedTrainerId
                trainerActive && matchesSelection
            }
            .sortedWith(
                compareBy<AdminScheduleSlot> { (it.trainerName ?: it.title).lowercase() }
                    .thenBy { it.startTime },
            )
    }
    val weekdays = AdminWeekday.WORK_WEEK
    val timeRows = remember(companyHours, filtered) {
        hourlyRowsFromCompanyHours(companyHours).ifEmpty {
            hourlyRowsFromSchedules(filtered)
        }
    }
    val enrolledByTrainerDay = remember(memberships, filtered, week) {
        val days = weekdays.map { week.dateForWeekday(it.iso).take(10) }
        enrolledCountsByTrainerDay(memberships, filtered.mapNotNull { it.trainerId }, days)
    }
    val weekBookings = remember(bookings, week, filtered) {
        val scheduleIds = filtered.map { it.id }.toSet()
        bookings.filter { b ->
            week.containsIsoDate(b.sessionDate) && b.scheduleId in scheduleIds
        }
    }

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(20.dp),
    ) {
        // Título arriba y navegador de semana debajo: en celular el título tenía casi ancho cero.
        Column(Modifier.fillMaxWidth()) {
            Text(t.dashboard.trainerSchedule, fontWeight = FontWeight.Bold, fontSize = 16.sp)
            Text(
                when {
                    loadingGrid -> t.common.loading
                    timeRows.isEmpty() -> t.dashboard.noSlotsToShow
                    else -> t.dashboard.weekBookingsSummary(weekBookings.size)
                },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            Spacer(Modifier.height(10.dp))
            WeekNavigator(week = week, onWeekOffsetChange = { weekOffset = it })
        }
        Spacer(Modifier.height(10.dp))
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            DashboardScheduleLegendSwatch(
                fill = AdminColors.SlotCard,
                border = AdminColors.Primary.copy(alpha = 0.35f),
                label = "Activo",
            )
            DashboardScheduleLegendSwatch(
                fill = absentColor.copy(alpha = 0.18f),
                border = absentColor,
                label = "Inasistencia",
            )
        }
        Spacer(Modifier.height(12.dp))
        if (loadingGrid) {
            Box(Modifier.fillMaxWidth().height(120.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary, modifier = Modifier.size(28.dp))
            }
        } else if (timeRows.isEmpty()) {
            Text(t.dashboard.noSlotsToShow, color = AdminColors.TextMuted, fontSize = 13.sp)
        } else {
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                // Celular: un día a la vez con selector de días; en escritorio se muestran todos.
                val compactDays = maxWidth < 600.dp
                var mobileDayIndex by remember { mutableStateOf(0) }
                val selectedIndex = mobileDayIndex.coerceIn(0, (weekdays.size - 1).coerceAtLeast(0))
                val shownDays = if (compactDays && weekdays.isNotEmpty()) listOf(weekdays[selectedIndex]) else weekdays
                // Botones de día y cuadrícula apilados (BoxWithConstraints superpone a sus hijos).
                val availableWidth = maxWidth
                Column(Modifier.fillMaxWidth()) {
                if (compactDays && weekdays.isNotEmpty()) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        weekdays.forEachIndexed { index, day ->
                            val selected = index == selectedIndex
                            Column(
                                Modifier
                                    .weight(1f)
                                    .clip(RoundedCornerShape(10.dp))
                                    .background(if (selected) AdminColors.Primary else AdminColors.SurfaceContainer)
                                    .clickable { mobileDayIndex = index }
                                    .padding(vertical = 6.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                            ) {
                                Text(
                                    t.common.weekdayShort(day),
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 12.sp,
                                    color = if (selected) AdminColors.OnBrand else AdminColors.Text,
                                )
                                Text(
                                    week.dateForWeekday(day.iso).takeLast(5).replace('-', '/'),
                                    fontSize = 10.sp,
                                    color = if (selected) AdminColors.OnBrand else AdminColors.TextMuted,
                                )
                            }
                        }
                    }
                    Spacer(Modifier.height(10.dp))
                }
                val timeColWidth = 56.dp
                val minDayWidth = 100.dp
                val dayCount = shownDays.size.coerceAtLeast(1)
                val dayColWidth = ((availableWidth - timeColWidth) / dayCount).coerceAtLeast(minDayWidth)
                val gridWidth = timeColWidth + dayColWidth * dayCount
                val needsScroll = gridWidth > availableWidth + 1.dp
                val horizontalScroll = rememberScrollState()
                Column(
                    Modifier
                        .fillMaxWidth()
                        .then(if (needsScroll) Modifier.horizontalScroll(horizontalScroll) else Modifier),
                ) {
                    // En celular el día ya aparece en los botones de arriba: no se repite el encabezado.
                    if (!compactDays) Row(
                        Modifier.width(gridWidth),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Spacer(Modifier.width(timeColWidth))
                        shownDays.forEach { day ->
                            val date = week.dateForWeekday(day.iso).takeLast(5).replace('-', '/')
                            Column(
                                Modifier.width(dayColWidth).padding(horizontal = 4.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                            ) {
                                Text(
                                    t.common.weekdayShort(day),
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 12.sp,
                                    color = AdminColors.TextMuted,
                                )
                                Text(date, fontSize = 10.sp, color = AdminColors.TextMuted)
                            }
                        }
                    }
                    HorizontalDivider(
                        Modifier.width(gridWidth).padding(vertical = 6.dp),
                        color = AdminColors.Outline.copy(alpha = 0.4f),
                    )
                    timeRows.forEach { timeKey ->
                        Row(
                            Modifier.width(gridWidth).padding(vertical = 4.dp),
                            verticalAlignment = Alignment.Top,
                        ) {
                            Text(
                                timeKey.replace('-', '\n'),
                                modifier = Modifier.width(timeColWidth).padding(top = 6.dp, end = 4.dp),
                                fontSize = 10.sp,
                                lineHeight = 12.sp,
                                color = AdminColors.TextMuted,
                                fontWeight = FontWeight.SemiBold,
                                textAlign = TextAlign.Center,
                            )
                            shownDays.forEach { day ->
                                val dayIso = week.dateForWeekday(day.iso)
                                val slots = filtered
                                    .filter { it.weekday == day.iso && slotOverlapsHourRow(it, timeKey) }
                                    .distinctBy { it.trainerId ?: it.id }
                                    .sortedBy { (it.trainerName ?: it.title).lowercase() }
                                Box(Modifier.width(dayColWidth).padding(horizontal = 4.dp)) {
                                    if (slots.isEmpty()) {
                                        Box(
                                            Modifier
                                                .fillMaxWidth()
                                                .height(44.dp)
                                                .clip(RoundedCornerShape(10.dp))
                                                .background(AdminColors.Outline.copy(alpha = 0.12f)),
                                        )
                                    } else {
                                        val anyAbsent = slots.any { it.trainerAbsent }
                                        Column(
                                            Modifier
                                                .fillMaxWidth()
                                                .clip(RoundedCornerShape(10.dp))
                                                .border(
                                                    1.dp,
                                                    if (anyAbsent) absentColor.copy(alpha = 0.7f)
                                                    else AdminColors.Primary.copy(alpha = 0.28f),
                                                    RoundedCornerShape(10.dp),
                                                )
                                                .background(
                                                    if (anyAbsent) absentColor.copy(alpha = 0.08f)
                                                    else AdminColors.SlotCard,
                                                )
                                                .padding(horizontal = 8.dp, vertical = 6.dp),
                                            verticalArrangement = Arrangement.spacedBy(4.dp),
                                        ) {
                                            slots.forEach { slot ->
                                                val enrolled = enrolledCountKey(slot.trainerId, dayIso)
                                                    ?.let { enrolledByTrainerDay[it] }
                                                    ?: 0
                                                val nameColor = if (slot.trainerAbsent) {
                                                    absentColor
                                                } else {
                                                    AdminColors.Text
                                                }
                                                Row(
                                                    Modifier.fillMaxWidth(),
                                                    horizontalArrangement = Arrangement.SpaceBetween,
                                                    verticalAlignment = Alignment.CenterVertically,
                                                ) {
                                                    Text(
                                                        slot.trainerName ?: slot.title,
                                                        modifier = Modifier.weight(1f),
                                                        fontWeight = FontWeight.SemiBold,
                                                        fontSize = 12.sp,
                                                        color = nameColor,
                                                        maxLines = 1,
                                                    )
                                                    Text(
                                                        if (slot.trainerId != null) "$enrolled" else "—",
                                                        fontSize = 12.sp,
                                                        fontWeight = FontWeight.Bold,
                                                        color = if (slot.trainerAbsent) absentColor else AdminColors.Primary,
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        HorizontalDivider(
                            Modifier.width(gridWidth),
                            color = AdminColors.Outline.copy(alpha = 0.2f),
                        )
                    }
                }
                }
            }
        }
    }
}

@Composable
private fun DashboardScheduleLegendSwatch(fill: Color, border: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        Box(
            Modifier
                .size(width = 18.dp, height = 14.dp)
                .clip(RoundedCornerShape(4.dp))
                .border(1.dp, border, RoundedCornerShape(4.dp))
                .background(fill),
        )
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    }
}

@Composable
private fun TrainerSidePanel(
    trainers: List<AdminTrainer>,
    selectedId: String?,
    onSelect: (String?) -> Unit,
    bookings: List<AdminBookingItem>,
    onAddTrainer: () -> Unit,
) {
    val t = strings()
    val selected = trainers.firstOrNull { it.id == selectedId }
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Column(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            if (selected != null) {
                val initials = selected.displayName.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
                Box(
                    Modifier.size(72.dp).clip(CircleShape).background(AdminColors.Primary),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(initials, color = AdminColors.OnBrand, fontWeight = FontWeight.Bold, fontSize = 22.sp)
                }
                Spacer(Modifier.height(12.dp))
                Text(selected.displayName, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Text(selected.role, color = AdminColors.TextMuted, fontSize = 12.sp)
                selected.email?.let { Text(it, color = AdminColors.TextMuted, fontSize = 12.sp) }
            } else {
                Text(t.dashboard.allTrainers, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Text(
                    t.dashboard.allTrainersHint,
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 6.dp),
                )
            }
        }

        Column(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(16.dp),
        ) {
            Text(
                t.dashboard.panelFilter(trainers.size),
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
            )
            Spacer(Modifier.height(10.dp))
            Row(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(if (selectedId == null) AdminColors.SlotCard else androidx.compose.ui.graphics.Color.Transparent)
                    .clickable { onSelect(null) }
                    .padding(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(t.common.all, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
            }
            if (trainers.isEmpty()) {
                Text(t.dashboard.noTrainers, color = AdminColors.TextMuted, fontSize = 12.sp)
            } else {
                trainers.forEach { t ->
                    val selectedRow = t.id == selectedId
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(10.dp))
                            .background(if (selectedRow) AdminColors.SlotCard else androidx.compose.ui.graphics.Color.Transparent)
                            .clickable { onSelect(t.id) }
                            .padding(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        val ini = t.displayName.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
                        Box(
                            Modifier.size(36.dp).clip(CircleShape).background(AdminColors.Primary.copy(alpha = 0.85f)),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(ini, color = AdminColors.OnBrand, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                        }
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(t.displayName, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                            Text(t.email ?: t.role, color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                }
            }
        }

        MiniCalendar(bookings)

        Button(
            onClick = onAddTrainer,
            modifier = Modifier.fillMaxWidth().height(48.dp),
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = AdminColors.Primary,
                contentColor = AdminColors.OnBrand,
            ),
        ) {
            Text(t.dashboard.addTrainer, fontWeight = FontWeight.Bold)
        }
    }
}

@Composable
private fun MiniCalendar(bookings: List<AdminBookingItem>) {
    val t = strings()
    val daysWithBookings = bookings.mapNotNull { it.sessionDate.takeLast(2).toIntOrNull() }.toSet()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(t.dashboard.calendarTitle, fontWeight = FontWeight.Bold, fontSize = 14.sp)
        Spacer(Modifier.height(10.dp))
        val cells = (1..28).toList()
        cells.chunked(7).forEach { week ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                week.forEach { day ->
                    val marked = day in daysWithBookings
                    Box(
                        Modifier
                            .size(28.dp)
                            .clip(CircleShape)
                            .background(if (marked) AdminColors.Primary else androidx.compose.ui.graphics.Color.Transparent),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            day.toString(),
                            color = if (marked) AdminColors.OnBrand else AdminColors.Text,
                            fontSize = 12.sp,
                            fontWeight = if (marked) FontWeight.Bold else FontWeight.Normal,
                        )
                    }
                }
            }
            Spacer(Modifier.height(4.dp))
        }
        if (bookings.isEmpty()) {
            Text(t.dashboard.noBookingsInPeriod, color = AdminColors.TextMuted, fontSize = 12.sp)
        }
    }
}

@Composable
fun AdminListScreen(route: AdminRoute) {
    val t = strings()
    var rows by remember { mutableStateOf<List<AdminListRow>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(route) {
        loading = true
        error = null
        try {
            rows = AdminRepo.api.listFor(route)
        } catch (e: Exception) {
            error = e.userMessage(t.common.listLoadFailed)
        } finally {
            loading = false
        }
    }

    Column(Modifier.fillMaxSize().background(AdminColors.Surface).padding(GymlySpace.lg)) {
        Text(strings().nav.route(route), fontSize = 22.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
        Text(
            AdminRepo.api.subtitleFor(route),
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
            modifier = Modifier.padding(vertical = GymlySpace.md),
        )
        error?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = GymlySpace.md))
        }
        if (loading) {
            GymlyLoadingCenter()
        } else {
            LazyColumn {
                if (rows.isEmpty()) {
                    item { Text(t.common.noRecords, color = AdminColors.TextMuted) }
                } else {
                    items(rows, key = { it.id }) { row ->
                        PortalListCard {
                            Text(row.title, fontWeight = FontWeight.Bold)
                            Text(row.subtitle, color = AdminColors.TextMuted, modifier = Modifier.padding(top = 4.dp))
                            row.badge?.let {
                                Text(it, color = AdminColors.Primary, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AdminPlaceholderScreen(route: AdminRoute) {
    Column(Modifier.fillMaxSize().background(AdminColors.Surface).padding(GymlySpace.lg)) {
        Text(strings().nav.route(route), fontSize = 22.sp, fontWeight = FontWeight.Bold)
        Spacer(Modifier.height(8.dp))
        Text(AdminRepo.api.subtitleFor(route), color = AdminColors.TextMuted, fontSize = 14.sp)
        Spacer(Modifier.height(24.dp))
        Box(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(24.dp),
        ) {
            Text("Módulo en preparación. La navegación ya está lista en el menú.", color = AdminColors.TextMuted)
        }
    }
}
